import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { track } from "@/lib/analytics";
import { SOCIALS, SKOOL_URL, CONTACT_EMAIL } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      setDone(true);
      setEmail("");
      toast.success("You're on the list.");
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-36 bg-[#050505]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20">
        {/* Newsletter */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-[12px] tracking-[0.3em] uppercase text-white/40 mb-5">Stay Close</p>
          <h2 className="font-heading uppercase tracking-tight text-white text-4xl lg:text-5xl leading-[0.95]">
            Get on the <span className="font-serif-accent italic lowercase normal-case tracking-normal text-gold">list</span>
          </h2>
          <p className="text-white/55 mt-5 leading-relaxed text-[15px] max-w-md">
            Drops, sessions, mixes and community updates — straight to your inbox, no noise.
          </p>

          {done ? (
            <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.03] p-6" data-testid="subscribe-success">
              <p className="text-white/80">You're in. Talk soon.</p>
            </div>
          ) : (
            <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md" data-testid="subscribe-form">
              <Input
                data-testid="subscribe-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="dark-input flex-1"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="subscribe-submit"
                className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gold text-black text-[12px] tracking-[0.14em] uppercase font-medium hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60"
              >
                Sign Up <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          )}
        </motion.div>

        {/* Direct contact */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <p className="text-[12px] tracking-[0.3em] uppercase text-white/40 mb-5">Contact</p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            onClick={() => track("contact_click", "email")}
            data-testid="contact-email"
            className="group flex items-center justify-between gap-4 py-5 border-t border-white/10 hover:px-2 transition-[padding] duration-300"
          >
            <span className="flex items-center gap-3 text-white text-lg lg:text-2xl font-heading uppercase tracking-tight">
              <Mail size={18} className="text-white/50" /> {CONTACT_EMAIL}
            </span>
            <ArrowUpRight size={20} className="text-white/40 group-hover:text-gold group-hover:rotate-45 transition-[transform,color] duration-300" />
          </a>

          <div className="mt-4 grid grid-cols-2 gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("contact_click", s.label.toLowerCase())}
                data-testid={`contact-social-${s.label.toLowerCase()}`}
                className="group flex items-center justify-between px-5 py-4 rounded-lg border border-white/10 hover:border-white/30 hover:bg-white/5 transition-colors duration-300"
              >
                <span className="text-[13px] tracking-[0.08em] uppercase text-white/70 group-hover:text-gold transition-colors">{s.label}</span>
                <ArrowUpRight size={15} className="text-white/40 group-hover:text-gold transition-colors" />
              </a>
            ))}
          </div>

          <a
            href={SKOOL_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("skool_click", "contact_section")}
            data-testid="contact-skool"
            className="mt-3 flex items-center justify-between px-5 py-4 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 transition-colors duration-300"
          >
            <span className="text-[13px] tracking-[0.08em] uppercase text-white">Join 808s &amp; Assets on Skool</span>
            <ArrowUpRight size={15} className="text-white/60" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
